import { computed, ref, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useModuleElectiveOfferings } from '@/composables/queries/useModuleElectiveOfferings';
import { useModuleElectiveCandidates } from '@/composables/queries/useModuleElectiveCandidates';
import { useModuleElectiveMutations } from '@/composables/mutations/tertiary/useModuleElectiveMutations';
import { useFormErrors } from '@/composables/useFormErrors';

export interface ModuleElectiveEditorProps {
  module: { id: string; name: string; academic_calendar_id?: string | null };
  calendars: { label: string; value: string }[];
}

export function useModuleElectiveEditor(props: ModuleElectiveEditorProps) {
  const moduleId = computed(() => props.module.id);
  const calendarId = ref<string | null>(props.module.academic_calendar_id ?? null);
  const search = ref('');
  const courseIds = ref<string[]>([]);
  const message = ref('');
  const failure = ref('');
  const { offerings, loading, error: listError, refetch } = useModuleElectiveOfferings(moduleId);
  const { courses, loading: searching, error: searchError } = useModuleElectiveCandidates(moduleId, calendarId, search);
  const { add, remove, adding, removing } = useModuleElectiveMutations();
  const { handleServerErrors, clearErrors, getErrorMessage } = useFormErrors();
  const v$ = useVuelidate({ calendarId: { required }, courseIds: { required, minLength: minLength(1) } }, { calendarId, courseIds });
  const options = computed(() => courses.value.filter(course => !offerings.value.some(offering =>
    offering.academic_calendar_id === calendarId.value && offering.course_id === course.id)).map(course => ({
    value: course.id, label: `${course.course_code} — ${course.course_title} (${course.unit} units)`,
  })));
  watch(() => props.module.id, () => { calendarId.value = props.module.academic_calendar_id ?? null; message.value = ''; failure.value = ''; });
  watch(calendarId, () => { courseIds.value = []; search.value = ''; clearErrors(); v$.value.$reset(); });
  async function save() {
    if (!await v$.value.$validate()) return;
    clearErrors(); failure.value = ''; message.value = '';
    try {
      await add({ moduleId: moduleId.value, calendarId: calendarId.value!, courseIds: courseIds.value });
      courseIds.value = []; v$.value.$reset();
      await refetch();
      message.value = 'Elective offerings saved. Eligible students can add them while this module is open.';
    } catch (error) {
      handleServerErrors(error);
      failure.value = error instanceof Error ? error.message : 'Unable to save elective offerings.';
    }
  }
  async function removeOffering(offeringId: string) {
    failure.value = ''; message.value = '';
    try {
      await remove({ moduleId: moduleId.value, offeringId });
      await refetch();
      message.value = 'Offering removed. Existing student registrations are preserved.';
    } catch (error) {
      failure.value = error instanceof Error ? error.message : 'Unable to remove this offering.';
    }
  }
  return { calendarId, search, courseIds, options, offerings, loading, searching, adding, removing, message, failure,
    listError, searchError, getErrorMessage, v$, save, removeOffering };
}
